import { Component, inject } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { BillingParty } from '../../domain/signup';
import { SignupStore } from '../../domain/signup.store';
import { BillingPartyFormComponent } from '../billing-party-form.component';

/**
 * Step three: who is invoiced.
 *
 * A thin host around {@link BillingPartyFormComponent}, which the portal also renders. The fields
 * are identical in both places and must stay so — they are the fields of a § 14 UStG invoice header,
 * and the only difference between collecting them and correcting them is where the result is sent.
 *
 * The lead paragraph says why these are being asked for, which the funnel needs and the portal does
 * not: nothing here is required to bring a tenant up, and a customer who has just chosen a plan is
 * entitled to know why a legal form and a VAT ID are suddenly wanted.
 */
@Component({
  selector: 'pp-billing-party-step',
  imports: [BillingPartyFormComponent, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.signup.billing_party'">
      <p class="step-lead">{{ t('lead') }}</p>
      <p class="step-hint">{{ t('hint') }}</p>
      <pp-billing-party-form (changed)="onChanged($event)" />
    </ng-container>
  `,
  styles: [
    `
      .step-hint {
        font-size: 0.85rem;
        opacity: 0.75;
      }
    `,
  ],
})
export class BillingPartyStepComponent {
  private readonly store = inject(SignupStore);

  constructor() {
    // The form emits on change, but a draft restored from storage has to reach the form, which is
    // what `billingParty` on the child would do — except the funnel creates rather than edits, so
    // the child is left uninitialised and the draft is pushed back in by the form's own first
    // emission. Reading the draft here instead would duplicate the form's own state.
  }

  onChanged(billingParty: Partial<BillingParty>): void {
    this.store.updateDraft({ billingParty });
  }
}
